import { useCallback, useEffect, useRef, useState } from 'react';
import { acquireTab, data, takeOverTab } from '../bridge/client';
import { language, t } from '../i18n/i18n';
import { Login } from './Login';
import { PerfHud } from './PerfHud';
import { hudEnabled, markFirstMeaningfulPaint } from './perf';
import { Workspace } from './Workspace';
import { setMediaBase } from './media';
import { useUiLanguage } from './features/settings/language';

type Phase = { kind: 'booting' } | { kind: 'otherTab' } | { kind: 'login' } | { kind: 'workspace' };

const CLIENT_ID = `web/${__APP_VERSION__}`;

function readCookie(name: string): string | null {
  for (const part of document.cookie.split(';')) {
    const [k, ...v] = part.trim().split('=');
    if (k === name) return decodeURIComponent(v.join('='));
  }
  return null;
}

/** '' = the chat server's own origin (production). `?server=` is for development only. */
function serverBase(): string {
  const fromQuery = new URLSearchParams(location.search).get('server');
  return fromQuery ?? '';
}

export function App() {
  const [phase, setPhase] = useState<Phase>({ kind: 'booting' });
  // Settings → Idioma applies live: the workspace is redrawn in the new language (URL keeps the place).
  const uiLanguage = useUiLanguage();
  const base = serverBase();

  const boot = useCallback(async () => {
    setMediaBase(base);
    data.start();
    const result = await data.call('boot', {
      baseUrl: base,
      token: null,
      clientId: CLIENT_ID,
      csrfToken: readCookie('MMCSRF'),
      language: language(),
      memoryOnly: new URLSearchParams(location.search).has('memory'),
    });
    console.info(`[satori-boot] storage=${result.storage} open=${Math.round(result.openMs)}ms schema=${result.schemaVersion} at=${Math.round(performance.now())}ms`);
    if (result.userId) {
      setPhase({ kind: 'workspace' });
      return;
    }
    // No account in the store. A live session COOKIE may still exist (signed in through another
    // tab, or the store was cleared): ask the server before showing a login form.
    if (readCookie('MMUSERID')) {
      const resumed = await data.call('resumeSession');
      if (resumed.userId) {
        setPhase({ kind: 'workspace' });
        return;
      }
    }
    setPhase({ kind: 'login' });
    markFirstMeaningfulPaint('login');
  }, [base]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      const mine = await acquireTab(() => setPhase({ kind: 'otherTab' }));
      if (!mine) {
        setPhase({ kind: 'otherTab' });
        markFirstMeaningfulPaint('other tab');
        return;
      }
      await boot();
    })();
  }, [boot]);

  const useHere = async () => {
    setPhase({ kind: 'booting' });
    await takeOverTab(() => setPhase({ kind: 'otherTab' }));
    await boot();
  };

  return (
    <>
      {phase.kind === 'booting' ? <div className="center-screen" aria-busy="true" aria-label={t('web.boot.loading')}><div className="wallpaper" /></div> : null}
      {phase.kind === 'otherTab' ? (
        <main className="center-screen">
          <div className="wallpaper" />
          <div className="card">
            <h1>{t('web.otherTab.title')}</h1>
            <p className="lede">{t('web.otherTab.body')}</p>
            <button className="primary-button" onClick={() => void useHere()}>
              {t('web.otherTab.useHere')}
            </button>
          </div>
        </main>
      ) : null}
      {phase.kind === 'login' ? (
        <Login
          onSignedIn={() => {
            data.call('setCsrfToken', readCookie('MMCSRF')).catch(() => undefined);
            setPhase({ kind: 'workspace' });
          }}
        />
      ) : null}
      {phase.kind === 'workspace' ? <Workspace key={uiLanguage} serverBase={base} onSignedOut={() => setPhase({ kind: 'login' })} /> : null}
      {hudEnabled() ? <PerfHud /> : null}
    </>
  );
}
